import { useEffect, useMemo, useState } from "react";

import { CompanyUploadPreview, CompanyUploadRow } from "@signalist/shared-types";

import { useCompanyPreviewMutation } from "@/pages/Private/redux/csvEnrichment/csvEnrichment.api";

import { CsvEnrichmentType } from "@/enum/csv-enrichment.enum";

import { FoundFieldMapping, matchHeaderIndex, ParsedXlsxData } from "./titleGate.utils";

const PREVIEW_DEBOUNCE_MS = 400;
const MAX_IDENTIFIER_LENGTH = 255;

/**
 * The mapped fields a company row can be exact-matched on, in the order the API prefers them.
 * `companyName` is last because it is the weakest of the three.
 */
const IDENTIFIER_FIELDS: { assignedField: string; key: keyof CompanyUploadRow }[] = [
	{ assignedField: "companyDomain", key: "domain" },
	{ assignedField: "companyLinkedinUrl", key: "linkedinUrl" },
	{ assignedField: "companyName", key: "name" },
];

interface UseCompanyUploadPreviewArgs {
	entityType?: string | null;
	foundFields?: FoundFieldMapping[];
	parsedData?: ParsedXlsxData;
}

interface UseCompanyUploadPreviewResult {
	preview?: CompanyUploadPreview;
	isPreviewLoading: boolean;
}

/**
 * PREC-3870 — the company-list twin of `useTitleScreeningPreview`: a debounced dry-run that tells
 * the XLS Statistics box how many of the uploaded companies Signalist already holds. Only fires
 * for an ORGANIZATION upload with at least one identifier column mapped, and is best-effort — the
 * box drops back to its three row figures rather than showing a wrong number.
 */
export const useCompanyUploadPreview = ({
	entityType,
	foundFields,
	parsedData,
}: UseCompanyUploadPreviewArgs): UseCompanyUploadPreviewResult => {
	const [companyPreview, { isLoading }] = useCompanyPreviewMutation();
	const [preview, setPreview] = useState<CompanyUploadPreview>();

	const columnIndexes = useMemo(() => {
		if (!parsedData || entityType !== CsvEnrichmentType.ORGANIZATION) {
			return [];
		}

		return IDENTIFIER_FIELDS.map(({ assignedField, key }) => {
			const field = foundFields?.find((mapped) => mapped.assignedField === assignedField);

			return {
				key,
				index: field ? matchHeaderIndex(parsedData.headers, field.foundField) : -1,
			};
		}).filter((column) => column.index >= 0);
	}, [entityType, foundFields, parsedData]);

	useEffect(() => {
		if (!columnIndexes.length || !parsedData?.rows.length) {
			setPreview(undefined);

			return;
		}

		const timer = setTimeout(async () => {
			const companies = parsedData.rows.map((row) =>
				Object.fromEntries(
					columnIndexes.map(({ key, index }) => [
						key,
						(row[index] ?? "").trim().slice(0, MAX_IDENTIFIER_LENGTH),
					])
				)
			);

			try {
				setPreview(await companyPreview({ companies }).unwrap());
			} catch {
				// Preview is best-effort — the box stays usable without the two company rows.
				setPreview(undefined);
			}
		}, PREVIEW_DEBOUNCE_MS);

		return () => clearTimeout(timer);
	}, [columnIndexes, parsedData, companyPreview]);

	return { preview, isPreviewLoading: isLoading };
};
